/**
 * Display formatting for RunProgress (ENG-369 / ENG-370).
 *
 * These helpers only format numbers the server already computed; they never
 * derive counts from events or jobs. The planning panel and every build
 * progress surface use them, so the same RunProgress reads the same everywhere.
 */

import type { RunEta, RunProgress, RunProgressStage, RunState } from "@mycompbox/shared";

/** Text shown while there is no usable estimate yet. */
export const ETA_ESTIMATING = "estimating…";
/** Text shown once the run has gone past its estimate. */
export const ETA_OVERRUN = "taking longer than usual";

/**
 * Format a duration in seconds as short text: "45s", "4m", "1h 20m".
 * Negative or non-finite values count as 0.
 */
export function formatDuration(totalSeconds: number): string {
  const s = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  if (s < 60) return `${String(s)}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`;
}

/**
 * Format a run ETA for display.
 *
 * - no estimate (null eta or null secondsRemaining): "estimating…"
 * - overrun: "taking longer than usual"
 * - under a minute: "<1 min left"
 * - under an hour: "~4 min left"
 * - an hour or more: "~1h 20m left"
 */
export function formatEta(eta: RunEta | null | undefined): string {
  if (!eta) return ETA_ESTIMATING;
  if (eta.overrun) return ETA_OVERRUN;
  const seconds = eta.secondsRemaining;
  if (seconds === null) return ETA_ESTIMATING;
  if (seconds < 60) return "<1 min left";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${String(minutes)} min left`;
  return `~${formatDuration(minutes * 60)} left`;
}

/**
 * Whole seconds between two ISO timestamps (end defaults to now).
 * Returns 0 when either timestamp is invalid or end is before start.
 */
export function secondsBetween(startIso: string, endIso?: string | null, now: number = Date.now()): number {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : now;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, Math.floor((end - start) / 1000));
}

/** Percent done (0-100) from RunProgress counts. Formatting only. */
export function runPercent(progress: RunProgress): number {
  if (progress.state === "completed") return 100;
  if (progress.total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((progress.completed / progress.total) * 100)));
}

/** The stage currently running, or null. */
export function currentRunStage(progress: RunProgress): RunProgressStage | null {
  if (progress.currentStageKey === null) return null;
  return progress.stages.find((s) => s.key === progress.currentStageKey) ?? null;
}

/** True for runs that ended without finishing (failed or cancelled). */
export function isUnsuccessfulRunState(state: RunState): boolean {
  return state === "failed" || state === "cancelled";
}

/** Explicit label for a finished, failed, cancelled or stalled run; null while running. */
export function runStateLabel(state: RunState): string | null {
  switch (state) {
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "stalled":
      return "Stalled";
    default:
      return null;
  }
}

/** "3 of 6 features done" for a build, "2 of 5 stages done" for a plan. */
export function runCountLabel(progress: RunProgress): string {
  const unit = progress.runKind === "build" ? "features" : "stages";
  return `${String(progress.completed)} of ${String(progress.total)} ${unit} done`;
}
